import { usePermissionAlert } from '@/composables/use-permission-alert';
import { PermissionState } from '@capacitor/core';
import { Geolocation } from '@capacitor/geolocation';
import { isPlatform } from '@ionic/vue';
import { captureException } from '@sentry/vue';
import { defineStore } from 'pinia';
import { computed, ref } from 'vue';

export const useLocationPermission = defineStore('location-permission', () => {
  const status = ref<PermissionState>('prompt');
  const isTurnedOn = ref(true);
  const { showAlert } = usePermissionAlert('Location');

  const shouldPrompt = computed(() => status.value === 'prompt' || status.value === 'prompt-with-rationale');
  const ready = computed(() => status.value === 'granted' && isTurnedOn.value);

  async function checkPermissionsWeb() {
    if ('geolocation' in navigator) {
      isTurnedOn.value = true;
      const { state } = await navigator.permissions.query({ name: 'geolocation' });
      status.value = state;
      if (state === 'granted') {
        // This is to cover a case where some browser's have the permission as granted
        // but the actual request for location will still fail
        await permissionPromptWeb();
      }
    } else {
      isTurnedOn.value = false;
      status.value = 'prompt';
    }
    console.log('%c Checked web location permissions', 'color:cyan', status.value);
  }

  async function checkPermissionsNative() {
    try {
      const permissions = await Geolocation.checkPermissions();
      console.log('%c Checked native location permissions', 'color:cyan', permissions);
      isTurnedOn.value = true;
      status.value = permissions.location;
    } catch (e) {
      console.log('%c Error checking native location permissions', 'color:cyan', e);
      isTurnedOn.value = false;
      if (e instanceof Error) {
        if (e.message.includes('Location services are not enabled.')) {
          status.value = 'denied';
        } else {
          captureException(e);
        }
      }
    }
  }

  async function onAppResume() {
    if (!isPlatform('hybrid')) {
      await checkPermissionsWeb();
    } else {
      await checkPermissionsNative();
    }
  }

  async function permissionPromptWeb() {
    const res = await new Promise<{ position?: GeolocationPosition; error?: any }>((resolve) => {
      navigator.geolocation.getCurrentPosition(
        (position) => resolve({ position }),
        (err) => resolve({ error: err }),
        { timeout: 3000 }
      );
    });
    console.log('%c Prompted web location permissions', 'color:cyan', res);
    if (res.position) {
      isTurnedOn.value = true;
      status.value = 'granted';
    } else if (res.error) {
      status.value = 'prompt';
      isTurnedOn.value = false;
    }
  }

  async function permissionPromptNative() {
    try {
      const permissions = await Geolocation.requestPermissions({ permissions: ['location'] });
      console.log('%c Prompted native location permissions', 'color:cyan', permissions);
      isTurnedOn.value = true;
      status.value = permissions.location;
    } catch (e) {
      console.log('%c Error requesting native location permissions', 'color:cyan', e);
      isTurnedOn.value = false;
      if (e instanceof Error) {
        if (e.message.includes('Location services are not enabled.')) {
          status.value = 'denied';
          showAlert();
        } else {
          captureException(e);
        }
      }
    }
  }

  function prompt() {
    if (!shouldPrompt.value) {
      console.log('%c skipping location prompt because status is', 'color:cyan', status.value);
      return Promise.resolve();
    }
    if (!isPlatform('hybrid')) {
      return permissionPromptWeb();
    } else {
      return permissionPromptNative();
    }
  }

  return {
    ready,
    shouldPrompt,
    prompt,
    onAppResume,
  };
});
